import { useMemo, useState } from "react";
import styles from "../styles/runner.module.css";
import type { MCQOption, MultipleChoiceConfig } from "../types";

interface Props {
  prompt: string;
  config: MultipleChoiceConfig;
  passingScore: number;
  onScored: (result: {
    score: number;
    passed: boolean;
    raw: { selected: string[]; correct_ids: string[] };
  }) => void;
}

function shuffleInPlace<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function MultipleChoice({ prompt, config, passingScore, onScored }: Props) {
  const options: MCQOption[] = useMemo(
    () => (config.shuffle ? shuffleInPlace(config.options) : config.options),
    [config.options, config.shuffle]
  );
  const correctIds = useMemo(
    () => config.options.filter((o) => o.correct).map((o) => o.id),
    [config.options]
  );
  const multi = Boolean(config.multi_select);

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);

  function toggle(id: string) {
    if (submitted) return;
    const next = new Set(selected);
    if (multi) {
      if (next.has(id)) next.delete(id);
      else next.add(id);
    } else {
      next.clear();
      next.add(id);
    }
    setSelected(next);
  }

  function submit() {
    if (submitted || selected.size === 0) return;
    // Jaccard-style score: |intersection| / |union|. Caps at 1.0.
    const sel = new Set(selected);
    const cor = new Set(correctIds);
    let inter = 0;
    for (const id of sel) if (cor.has(id)) inter += 1;
    const union = new Set<string>([...sel, ...cor]);
    const s = union.size === 0 ? 0 : inter / union.size;
    const ok = s >= passingScore;
    setScore(s);
    setPassed(ok);
    setSubmitted(true);
    onScored({
      score: s,
      passed: ok,
      raw: { selected: [...sel], correct_ids: [...cor] },
    });
  }

  return (
    <div className={styles.col}>
      <h2 className={styles.prompt}>{prompt}</h2>
      <ul className={styles.optionList}>
        {options.map((o) => {
          const isSelected = selected.has(o.id);
          const className = [
            styles.optionButton,
            isSelected && !submitted ? styles.optionSelected : "",
            submitted && o.correct ? styles.optionCorrect : "",
            submitted && isSelected && !o.correct ? styles.optionWrong : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <li key={o.id}>
              <button
                type="button"
                className={className}
                onClick={() => toggle(o.id)}
                disabled={submitted}
              >
                {o.label}
              </button>
            </li>
          );
        })}
      </ul>
      {!submitted ? (
        <button
          type="button"
          className={styles.primaryButton}
          disabled={selected.size === 0}
          onClick={submit}
        >
          {multi ? "Validar selección" : "Confirmar respuesta"}
        </button>
      ) : (
        <div className={passed ? styles.summary : `${styles.summary} ${styles.summaryFail}`}>
          <span className={styles.scoreBadge}>{Math.round(score * 100)}%</span>
          <strong>{passed ? "¡Buen trabajo!" : "Sigue intentando"}</strong>
          <span>
            Puntaje mínimo: {Math.round(passingScore * 100)}%
          </span>
        </div>
      )}
    </div>
  );
}
